import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import queryString from "query-string";
import { Helmet } from "react-helmet";
import classNames from "classnames";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Skeleton from "react-loading-skeleton";
import axios from "@dreamchimney/react-axios";
import { getSleevesByTag } from "~/api/getSleeves";
import SleeveResult from "~/Components/SleeveResult";
import SleeveMini from "~/Components/SleeveMini";
import RelatedTags from "~/Components/RelatedTags";
import Pager from "../../Components/Global/Pager";

const RELATED_TAGS_LIMIT = 8;

const getRelatedTagNames = (sleeves, currentTag, limit = RELATED_TAGS_LIMIT) => {
  const counts = {};
  const current = String(currentTag || "")
    .trim()
    .toLowerCase();

  sleeves.forEach((sleeve) => {
    const raw = sleeve.tags || sleeve.notes || [];
    const list = Array.isArray(raw) ? raw : String(raw).split(",");
    list.forEach((entry) => {
      const name = String(entry || "").trim();
      if (!name) return;
      if (name.toLowerCase() === current) return;
      counts[name] = (counts[name] || 0) + 1;
    });
  });

  return Object.keys(counts)
    .sort((a, b) => counts[b] - counts[a] || a.localeCompare(b))
    .slice(0, limit);
};

const TagListPage = ({ match, location }) => {
  const tag = match.params.tag;
  const history = useHistory();

  const [viewMode, setViewMode] = useState("gallery");
  const [page, setPage] = useState(1);
  const [sleeves, setSleeves] = useState([]);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const hasMore = sleeves.length < total;
  const pages = Math.ceil(total / perPage) || 1;
  const relatedTagNames = useMemo(() => getRelatedTagNames(sleeves, tag), [sleeves, tag]);
  const showRelatedTags = viewMode === "gallery" && !hasMore && !loading && !loadingMore && relatedTagNames.length > 0;

  // New tag: restart from page 1 (or URL page in list view)
  useEffect(() => {
    setSleeves([]);
    setTotal(0);
    if (viewMode === "list") {
      setPage(parseInt(queryString.parse(location.search).page || 1, 10) || 1);
    } else {
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag]);

  // Fetch whenever tag, page, or view mode changes
  useEffect(() => {
    let cancelled = false;
    const append = viewMode === "gallery" && page > 1;

    const load = async () => {
      if (append) {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await getSleevesByTag(axios, tag, page);
        if (cancelled) return;

        const result = response.data || {};
        const nextSleeves = result.sleeves || [];
        setTotal(result.total || 0);
        setPerPage(result.perPage || 10);
        setSleeves((prev) => (append ? [...prev, ...nextSleeves] : nextSleeves));
      } catch (e) {
        console.log("failure:", e);
        if (!cancelled && !append) {
          setSleeves([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
          loadingMoreRef.current = false;
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [tag, page, viewMode]);

  // Gallery infinite scroll
  useEffect(() => {
    if (viewMode !== "gallery") return undefined;

    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry || !entry.isIntersecting) return;
        if (loading || loadingMoreRef.current || !hasMore) return;
        setPage((prev) => prev + 1);
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [viewMode, loading, hasMore, sleeves.length]);

  const updatePageCallBack = (toPage) => {
    history.push("?page=" + toPage);
    setPage(toPage);
  };

  const setGalleryView = () => {
    if (viewMode === "gallery") return;
    history.replace(location.pathname);
    setSleeves([]);
    setPage(1);
    setViewMode("gallery");
  };

  const setListView = () => {
    if (viewMode === "list") return;
    history.replace("?page=1");
    setSleeves([]);
    setPage(1);
    setViewMode("list");
  };

  return (
    <div className="page-home">
      <Helmet>
        <title>Sleevery | Sleeves: {tag} | Dream Chimney</title>
      </Helmet>
      <Row>
        <Col xs={12}>
          <h1 className="page-header">
            <i className="fa fa-tags"></i> Sleeves: {tag}
          </h1>
        </Col>
        <Col xs={12}>
          <div className="view-toggle">
            <button
              type="button"
              className={classNames("view-toggle-btn", { active: viewMode === "gallery" })}
              onClick={setGalleryView}
              aria-label="Gallery view"
              title="Gallery view"
            >
              <i className="fa fa-th"></i>
            </button>
            <button
              type="button"
              className={classNames("view-toggle-btn", { active: viewMode === "list" })}
              onClick={setListView}
              aria-label="List view"
              title="List view"
            >
              <i className="fa fa-list"></i>
            </button>
          </div>
        </Col>
        <Col xs={12}>
          {loading && sleeves.length === 0 ? (
            <Skeleton count={3} />
          ) : sleeves.length === 0 ? null : (
            <Row>
              <Col xs={12}>
                <ul className={classNames("results", viewMode === "gallery" ? "cover-wall" : "cover-list")}>
                  {sleeves.map((sleeve) => (
                    <li key={sleeve.id}>
                      {viewMode === "gallery" ? (
                        <SleeveMini sleeve={sleeve} showCaption />
                      ) : (
                        <SleeveResult sleeve={sleeve} />
                      )}
                    </li>
                  ))}
                </ul>
              </Col>

              {viewMode === "gallery" && (
                <Col xs={12}>
                  <div ref={sentinelRef} className="gallery-scroll-sentinel" aria-hidden="true" />
                  {loadingMore && (
                    <div style={{ padding: "12px 0", textAlign: "center" }}>
                      <Skeleton count={1} />
                    </div>
                  )}
                </Col>
              )}

              {showRelatedTags && (
                <Col xs={12}>
                  <RelatedTags tags={relatedTagNames} />
                </Col>
              )}

              {viewMode === "list" && (
                <Col xs={12}>
                  <div style={{ padding: "20px 0" }}>
                    <Pager className="pagination-footer" page={page} pages={pages} updatePage={updatePageCallBack} />
                  </div>
                </Col>
              )}
            </Row>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default TagListPage;
